import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as httpService from "../services/httpService";
import { Employee_Api_EndPoints } from "../global/end-points";
import * as userService from "../utils/userService";
import LoadingSpinner from "../components/common/loadingSpinner";
import NoDoughCardMessage from "../components/common/nodoughcard_message";
import { FaExclamationTriangle } from "react-icons/fa";
import adminNumberGuide from "../assets/images/card-admin-number-guide-generated.png";

const CARD_REGISTRATION_STATUS = "CardRegistrationStatus";
const CARD_REGISTERED = "registered";
const CARD_NOT_REGISTERED = "not_registered";
const EWA_TERMS_AND_CONDITIONS_URL = "/ewa/ewatermsandconditions.htm";
const ADMIN_NUMBER_LENGTH = 8;

function AssignCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cardLast4, setCardLast4] = useState("");
  const [adminNumber, setAdminNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  const isOBO = !!sessionStorage.getItem("OnBehalfUserId");
  const cachedCardStatus = sessionStorage.getItem(CARD_REGISTRATION_STATUS);
  const [isCheckingCard, setIsCheckingCard] = useState(!isOBO && !cachedCardStatus);
  const [alreadyRegistered, setAlreadyRegistered] = useState(cachedCardStatus === CARD_REGISTERED);
  const [message, setMessage] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [termsMode, setTermsMode] = useState("view");

  const onAdminNumberChange = (event) => setAdminNumber(event.target.value.replace(/\D/g, "").slice(0, 20));
  const onCardLast4Change = (event) => setCardLast4(event.target.value.replace(/\D/g, "").slice(0, 4));

  const getErrorMessage = (response) => {
    const data = response?.response?.data || response?.data;
    if (typeof data === "string") {
      return data.toLowerCase() === "badrequest" ? t("cardRegisterDetailsMismatch") : data;
    }
    
    const rawMessage = data?.message || data?.title || data?.errorMessage || data?.ErrorMessage;
    if (typeof rawMessage === "string" && rawMessage.toLowerCase() === "badrequest") {
      return t("cardRegisterDetailsMismatch");
    }

    return data?.message || data?.title || data?.errorMessage || data?.ErrorMessage || t("cardRegisterGenericError");
  };

  const getCardAssignmentMessage = (result) => {
    const errorMessage = result?.ErrorMessage || result?.errorMessage || result?.message || "";
    const errorCode = result?.ErrorCode || result?.errorCode || "";

    if (errorCode === "400" || errorMessage.toLowerCase() === "badrequest") {
      return t("cardRegisterDetailsMismatch");
    }

    return errorMessage || t("cardRegisterGenericError");
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const parseResponseBody = (responseBody) => {
    if (typeof responseBody !== "string") return responseBody;

    try {
      return JSON.parse(responseBody);
    } catch {
      return responseBody;
    }
  };

  const profileValue = (profile, ...keys) => {
    for (const key of keys) {
      const value = profile?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }

    return "";
  };

  const getUserProfileForCard = async () => {
    const userId = sessionStorage.getItem("UserId");
    if (!userId) return {};

    const response = await httpService.Get(Employee_Api_EndPoints.GET_USER_INFO, {
      UserId: Number(userId),
    });

    return response?.status === 200 && response?.data ? response.data : {};
  };

  const getEmployeeIdForTerms = (profile) => {
    const employeeId = profileValue(profile, "EmployeeId", "employeeId") || sessionStorage.getItem("EmployeeId") || "";
    return /^\d+$/.test(employeeId) ? Number(employeeId) : null;
  };

  const saveEwaTermsAndConditionsStatus = async (profile, status) => {
    const employeeId = getEmployeeIdForTerms(profile);
    if (!employeeId) return null;

    const body = {
      EmployeeId: employeeId,
      EwaAcceptanceStatus: status,
      EwaAcceptanceDateTime: new Date().toISOString(),
      KioskId: null,
      StoreNumber: null,
    };

    return httpService.Post(Employee_Api_EndPoints.ACCEPT_CARD_REGISTRATION_EWA_TERMS, body);
  };

  useEffect(() => {
    if (isOBO) {
      navigate("/home", { replace: true });
    }
  }, [isOBO, navigate]);

  useEffect(() => {
    const checkExistingCard = async () => {
      if (isOBO) {
        setIsCheckingCard(false);
        return;
      }

      const cachedStatus = sessionStorage.getItem(CARD_REGISTRATION_STATUS);
      if (cachedStatus) {
        setAlreadyRegistered(cachedStatus === CARD_REGISTERED);
        setIsCheckingCard(false);
        return;
      }

      const phoneNumber = sessionStorage.getItem("phoneNumber");
      if (!phoneNumber) {
        setIsCheckingCard(false);
        userService.ClearSession();
        return;
      }

      const response = await httpService.CallWithHeaders(
        "get",
        Employee_Api_EndPoints.CARD_ACCOUNT_STATUS,
        { phoneNumber },
        { SecretKey: "DoUgHPaYSeCrEKeY" }
      );

      const hasCard = response?.status === 200 && response?.data?.hasCard === true;
      sessionStorage.setItem(CARD_REGISTRATION_STATUS, hasCard ? CARD_REGISTERED : CARD_NOT_REGISTERED);
      setAlreadyRegistered(hasCard);
      setIsCheckingCard(false);
    };

    checkExistingCard();
  }, []);

  const pollCardAssignmentResult = async (requestId, maxRetries = 5, delay = 3000) => {
    let lastStatus = "";

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const pollResponse = await httpService.CallWithHeaders(
        "post",
        Employee_Api_EndPoints.GET_RESPONSE_BY_REQUESTID,
        { requestID: requestId },
        { SecretKey: "DoUgHPaYSeCrEKeY" }
      );

      if (!pollResponse || pollResponse.status !== 200) {
        if (attempt < maxRetries - 1) await sleep(delay);
        continue;
      }

      const wrapper = pollResponse.data || {};
      const status = wrapper.status || wrapper.Status || "";
      if (status) lastStatus = status;

      const result = parseResponseBody(wrapper.response || wrapper.Response || wrapper.ResponseData);

      if (status === "Completed") {
        const errorFound = result?.ErrorFound === true || result?.errorFound === true;
        if (errorFound) {
          return {
            success: false,
            message: getCardAssignmentMessage(result),
          };
        }

        return { success: true };
      }

      if (status === "Pending" || status === "InProgress") {
        if (attempt < maxRetries - 1) await sleep(delay);
        continue;
      }

      return {
        success: false,
        message: wrapper?.message || t("cardRegisterGenericError"),
      };
    }

    return {
      success: false,
      message:
        lastStatus === "Pending" || lastStatus === "InProgress"
          ? t("cardRegisterStillProcessing")
          : t("cardRegisterConfirmError"),
    };
  };

  const registerCard = async (profile, recordAcceptedTerms = false) => {
    const phoneNumber = sessionStorage.getItem("phoneNumber");
    if (!phoneNumber) {
      setIsSubmitting(false);
      userService.ClearSession();
      return;
    }

    setIsSubmitting(true);
    const validationResponse = await httpService.Post(Employee_Api_EndPoints.VALIDATE_CARD, {
      adminNumber: Number(adminNumber),
    });
    const validatedCard = validationResponse?.data;
    if (validationResponse?.status !== 200 || validatedCard?.errorFound !== false) {
      const errorData = validationResponse?.response?.data || validatedCard;
      const errorMessage = typeof errorData === "string"
        ? errorData
        : errorData?.errorMessage || errorData?.ErrorMessage || errorData?.message || "";
      const invalidAdminNumber = /(?:invalid|unknown|unrecognized|not recognized|not found|does not exist|cannot be found|could not be found).*admin\s*number|admin\s*number.*(?:invalid|unknown|unrecognized|not recognized|not found|does not exist|cannot be found|could not be found)|card\s+(?:was\s+)?not found/i.test(errorMessage);
      setIsSubmitting(false);
      setMessage({
        title: t(invalidAdminNumber ? "adminNumberNotRecognizedTitle" : "cardDetailsNeedReview"),
        content: t(invalidAdminNumber ? "adminNumberNotRecognizedMessage" : "cardValidationFailed"),
      });
      return;
    }

    const returnedLastFour = typeof validatedCard.cardNumber === "string"
      ? validatedCard.cardNumber.trim().match(/\d{4}$/)?.[0]
      : null;
    if (!returnedLastFour) {
      setIsSubmitting(false);
      setMessage({ title: t("cardDetailsNeedReview"), content: t("cardValidationFailed") });
      return;
    }
    if (returnedLastFour !== cardLast4) {
      setIsSubmitting(false);
      setMessage({ title: t("cardDetailsNeedReview"), content: t("cardLastFourMismatch") });
      return;
    }

    const cardProfile = profile || await getUserProfileForCard();
    const userId = sessionStorage.getItem("UserId");
    const body = {
      RequestId: window.crypto?.randomUUID?.() || `web-${Date.now()}`,
      UserId: userId ? Number(userId) : null,
      PhoneNumber: phoneNumber,
      AdminNumber: adminNumber,
      LastFourDigits: cardLast4,
      FirstName: profileValue(cardProfile, "FirstName", "firstName"),
      LastName: profileValue(cardProfile, "LastName", "lastName"),
      Email: profileValue(cardProfile, "Email", "email"),
      AddressLine1: profileValue(cardProfile, "AddressLine1", "addressLine1", "Address", "address"),
      AddressLine2: profileValue(cardProfile, "AddressLine2", "addressLine2"),
      AddressLine3: profileValue(cardProfile, "AddressLine3", "addressLine3"),
      City: profileValue(cardProfile, "City", "city"),
      State: profileValue(cardProfile, "State", "state"),
      ZipCode: profileValue(cardProfile, "ZipCode", "zipCode", "Zip", "zip", "PostalCode", "postalCode"),
      DateOfBirth: profileValue(cardProfile, "DateOfBirth", "dateOfBirth"),
      IdNumber: profileValue(cardProfile, "SocialSecurityNumber", "socialSecurityNumber", "SsnNumber", "ssnNumber"),
    };

    const response = await httpService.CallWithHeaders(
      "post",
      Employee_Api_EndPoints.CREATE_TRANSCARD_REQUEST,
      body,
      { SecretKey: "DoUgHPaYSeCrEKeY" }
    );

    if (response?.status !== 200 || response?.data?.errorFound === true || response?.data?.ErrorFound === true) {
      setIsSubmitting(false);
      setMessage({ title: t("cardDetailsNeedReview"), content: getErrorMessage(response) });
      return;
    }

    const requestId =
      response?.data?.RequestId ||
      response?.data?.requestId ||
      response?.data?.RequestID ||
      body.RequestId;

    const result = await pollCardAssignmentResult(requestId);

    if (result.success) {
      if (recordAcceptedTerms) {
        const termsResponse = await saveEwaTermsAndConditionsStatus(cardProfile, "Accepted");

        if (termsResponse?.status !== 200) {
          setIsSubmitting(false);
          const content = termsResponse === null ? t("termsAcceptanceMissingEmployee") : t("termsAcceptanceFailedMessage");
          setMessage({ title: t("termsAcceptanceFailedTitle"), content });
          return;
        }
      }

      setIsSubmitting(false);
      sessionStorage.setItem(CARD_REGISTRATION_STATUS, CARD_REGISTERED);
      setAlreadyRegistered(true);
      navigate("/doughsummary", {
        state: {
          cardRegistered: true,
        },
      });
      return;
    }

    setIsSubmitting(false);
    setMessage({ title: t("cardDetailsNeedReview"), content: result.message });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (isOBO) {
      navigate("/home", { replace: true });
      return;
    }

    if (alreadyRegistered) {
      setMessage({ title: t("cardAlreadyRegisteredTitle"), content: t("cardAlreadyRegisteredMessage") });
      return;
    }

    if (cardLast4.length !== 4 || !adminNumber) return;

    if (adminNumber.length !== ADMIN_NUMBER_LENGTH) {
      setMessage({ title: t("cardDetailsNeedReview"), content: t("adminNumberMinimumLength") });
      return;
    }

    setTermsMode("register");
    setShowTerms(true);
  };

  const acceptTermsAndRegister = async () => {
    setShowTerms(false);
    setIsSubmitting(true);
    const profile = await getUserProfileForCard();
    await registerCard(profile, true);
  };

  const closeTerms = () => {
    setShowTerms(false);
  };

  const declineTerms = async () => {
    setShowTerms(false);

    if (termsMode !== "register") return;

    setIsSavingTerms(true);
    const profile = await getUserProfileForCard();
    const termsResponse = await saveEwaTermsAndConditionsStatus(profile, "Declined");
    setIsSavingTerms(false);

    if (termsResponse?.status !== 200) {
      const content = termsResponse === null ? t("termsAcceptanceMissingEmployee") : t("termsResponseFailedMessage");
      setMessage({ title: t("termsResponseFailedTitle"), content });
    }
  };

  const viewTerms = () => {
    setTermsMode("view");
    setShowTerms(true);
  };

  if (isOBO) return null;

  const pageTitle = alreadyRegistered
      ? t("cardAlreadyRegisteredTitle")
      : t("registerYourCard");

  return (
    <Fragment>
      {(isSubmitting || isCheckingCard) && (
        <LoadingSpinner
          className="assign-card-loading"
          message={isCheckingCard ? t("checkingCardRegistration") : t("verifyingCardDetails")}
          description={isCheckingCard ? t("checkingCardRegistrationDescription") : t("verifyingCardDetailsDescription")}
        />
      )}
      <div className="dashboard-container">
        {!isCheckingCard && (
          <>
            <h2 className="page-module-heading mb-15">{pageTitle}</h2>
            {alreadyRegistered ? (
              <div className="dough-card-draw-option assign-card-panel">
                <p className="draw-question-label">{t("cardAlreadyRegisteredMessage")}</p>
                <button
                  className="btn load-card-btn assign-card-primary-action"
                  type="button"
                  onClick={() => navigate("/doughsummary")}
                >
                  {t("goToLoadFunds")}
                </button>
                <p className="assign-card-terms-note">
                  {t("viewTermsPrompt")}{" "}
                  <button
                    className="assign-card-terms-link"
                    type="button"
                    onClick={viewTerms}
                  >
                    {t("termsAndConditions")}
                  </button>
                </p>
              </div>
            ) : (
              <div className="dough-card-draw-option assign-card-panel">
                <p className="draw-question-label">
                  {t("registerCardIntro")}
                </p>
                <div className="admin-number-guide">
                  <div className="admin-number-guide-image-wrap">
                    <img src={adminNumberGuide} alt={t("adminNumberGuideAlt")} />
                  </div>
                </div>
                <form className="wage-input-container" onSubmit={submit}>
                  <label className="enter-amount-label" htmlFor="admin-number">{t("adminNumber")}</label>
                  <p className="assign-card-field-helper">
                    {t("adminNumberLocation")}
                  </p>
                  <input
                    id="admin-number"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={adminNumber}
                    onChange={onAdminNumberChange}
                    className="input-wage"
                    placeholder={t("enterAdminNumber")}
                    disabled={isSubmitting}
                    maxLength={20}
                    required
                  />
                  <label className="enter-amount-label" htmlFor="card-last4">{t("last4DigitsOfYourCard")}</label>
                  <input
                    id="card-last4"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={cardLast4}
                    onChange={onCardLast4Change}
                    className="input-wage"
                    placeholder={t("enterLast4Digits")}
                    maxLength={4}
                    disabled={isSubmitting}
                    required
                  />
                  <button className="btn load-card-btn" type="submit" disabled={isSubmitting || !adminNumber || cardLast4.length !== 4}>
                    {t("registerCard")}
                  </button>
                  <p className="assign-card-terms-note">
                    {t("viewTermsPrompt")}{" "}
                    <button
                      className="assign-card-terms-link"
                      type="button"
                      onClick={viewTerms}
                    >
                      {t("termsAndConditions")}
                    </button>
                  </p>
                </form>
              </div>
            )}
          </>
        )}
      </div>
      {message && (
        <NoDoughCardMessage
          title={message.title}
          className="assign-card-error-popup"
          message={
            <div className="assign-card-error-content">
              <div className="assign-card-error-icon" aria-hidden="true">
                <FaExclamationTriangle />
              </div>
              <p>{message.content}</p>
            </div>
          }
          onClose={() => setMessage(null)}
        />
      )}
      {showTerms && (
        <>
          <div className="popup-backdrop"></div>
          <div className="ewa-terms-popup">
            <div className="ewa-terms-popup-header">
              <h2 className="ewa-terms-popup-heading">{t("termsAndConditions")}</h2>
              {termsMode === "view" && (
                <button
                  onClick={closeTerms}
                  className="close"
                  type="button"
                  aria-label={t("close")}
                ></button>
              )}
            </div>
            <div className="ewa-terms-popup-body">
              <iframe
                src={EWA_TERMS_AND_CONDITIONS_URL}
                title={t("termsAndConditions")}
                className="ewa-terms-frame"
              />
            </div>
            {termsMode === "register" && (
              <div className="ewa-terms-popup-actions">
                  <button className="btn ewa-terms-decline-btn" type="button" onClick={declineTerms} disabled={isSavingTerms}>
                    {t("decline")}
                  </button>
                  <button className="btn load-card-btn ewa-terms-accept-btn" type="button" onClick={acceptTermsAndRegister} disabled={isSavingTerms}>
                    {t("acceptAndRegister")}
                  </button>
              </div>
            )}
          </div>
        </>
      )}
    </Fragment>
  );
}

export default AssignCard;


